import { inject, Injectable } from '@angular/core';

import {
  SPONSOR_ONBOARDING_PATH_BY_STEP_NUMBER,
  SPONSOR_ONBOARDING_STEP_PATHS,
  SPONSOR_SECTOR_OTHER_VALUE,
} from '../constants/sponsor-onboarding.constants';
import type { ISponsorOnboardingDraft } from '../models/sponsor-onboarding-draft.interface';
import { SponsorGeographyStepService } from './sponsor-geography-step.service';
import { SponsorOnboardingDraftService } from './sponsor-onboarding-draft.service';
import { SponsorOnboardingHydrateService } from './sponsor-onboarding-hydrate.service';

@Injectable({
  providedIn: 'root',
})
export class SponsorOnboardingValidationService {
  private readonly draftService = inject(SponsorOnboardingDraftService);
  private readonly hydrateService = inject(SponsorOnboardingHydrateService);
  private readonly geographyStep = inject(SponsorGeographyStepService);

  public getResumeStepPath(): string {
    if (this.hydrateService.statusCompleted()) {
      return SPONSOR_ONBOARDING_STEP_PATHS.summary;
    }
    const serverStep = this.hydrateService.statusCurrentStep();
    const serverPath = SPONSOR_ONBOARDING_PATH_BY_STEP_NUMBER[serverStep];
    if (serverPath) {
      return serverPath;
    }
    return this.getFirstIncompleteStepPath() ?? SPONSOR_ONBOARDING_STEP_PATHS.goals;
  }

  public getFirstIncompleteStepPath(draft?: ISponsorOnboardingDraft): string | null {
    const d = draft ?? this.draftService.draft();
    if (!this.isGoalsStepValid(d)) {
      return SPONSOR_ONBOARDING_STEP_PATHS.goals;
    }
    if (!this.isAudienceStepValid(d)) {
      return SPONSOR_ONBOARDING_STEP_PATHS.audience;
    }
    if (!this.isGeographyStepValid(d)) {
      return SPONSOR_ONBOARDING_STEP_PATHS.geography;
    }
    if (!this.isSectorStepValid(d)) {
      return SPONSOR_ONBOARDING_STEP_PATHS.sector;
    }
    if (!this.isCampaignBudgetStepValid(d)) {
      return SPONSOR_ONBOARDING_STEP_PATHS.budgetDuration;
    }
    return null;
  }

  public isOnboardingReadyForSummary(draft?: ISponsorOnboardingDraft): boolean {
    return this.getFirstIncompleteStepPath(draft) === null;
  }

  public isGoalsStepValid(draft?: ISponsorOnboardingDraft): boolean {
    const goals = (draft ?? this.draftService.draft()).goals;
    if (!goals.primary_goal) {
      return false;
    }
    if (
      goals.secondary_goal !== null &&
      goals.secondary_goal === goals.primary_goal
    ) {
      return false;
    }
    return true;
  }

  public isAudienceStepValid(draft?: ISponsorOnboardingDraft): boolean {
    const audience = (draft ?? this.draftService.draft()).audience;
    if (!audience.primary_audience) {
      return false;
    }
    if (
      audience.secondary_audience !== null &&
      audience.secondary_audience === audience.primary_audience
    ) {
      return false;
    }
    return true;
  }

  public isGeographyStepValid(draft?: ISponsorOnboardingDraft): boolean {
    return this.geographyStep.isGeographyDraftValid(
      (draft ?? this.draftService.draft()).geography,
    );
  }

  public isSectorStepValid(draft?: ISponsorOnboardingDraft): boolean {
    const sector = (draft ?? this.draftService.draft()).sector;
    if (!sector.sector) {
      return false;
    }
    if (
      sector.sector === SPONSOR_SECTOR_OTHER_VALUE &&
      !(sector.sector_other ?? '').trim()
    ) {
      return false;
    }
    return true;
  }

  public isCampaignBudgetStepValid(draft?: ISponsorOnboardingDraft): boolean {
    const budget = (draft ?? this.draftService.draft()).campaign_budget;
    return Boolean(budget.budget_range && budget.campaign_duration);
  }

  public isCompanyActionsStepValid(draft?: ISponsorOnboardingDraft): boolean {
    return (draft ?? this.draftService.draft()).company_actions.company_actions.length > 0;
  }
}
